import { useEffect, useRef } from 'react'
import { usePathname } from '@/routing/navigation'
import { useFeedback } from './FeedbackContext'
import { isFeedbackCoolingDown } from './FeedbackToast'
import {
  FEEDBACK_TRIGGERS,
  isTriggerCoolingDown,
  setTriggerCooldown,
  isGlobalCooldownActive,
  setGlobalCooldown,
  FeedbackTrigger,
} from './feedback-triggers'
import { ArmedAutoFeedback } from './feedback-occlusion'
import {
  getLastAnalyticsEvent,
  track,
  AnalyticsEvents,
} from '@/lib/services/analytics'

/**
 * Polls `getLastAnalyticsEvent()` to arm auto feedback.
 *
 * A corner-dense surface (survey charts, twin chat, analyst composer) does
 * not receive the toast. The prompt is held, without starting its cooldown,
 * until the corner is free again — another results tab, or the next page.
 * Dismiss and per-trigger cooldowns are unchanged once the toast actually opens.
 */
export function useFeedbackTrigger() {
  const { isOpen, open, cornerOccupied } = useFeedback()
  const pathname = usePathname()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSeenEventRef = useRef<string | null>(null)
  const isOpenRef = useRef(isOpen)
  const cornerOccupiedRef = useRef(cornerOccupied)
  const armedRef = useRef<ArmedAutoFeedback | null>(null)
  const openRef = useRef(open)
  isOpenRef.current = isOpen
  cornerOccupiedRef.current = cornerOccupied
  openRef.current = open

  function clearPendingTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  function trackSuppressed(triggerEvent: string, reason: string) {
    track(AnalyticsEvents.FEEDBACK_AUTO_SUPPRESSED, {
      trigger_event: triggerEvent,
      reason,
    })
  }

  function holdForCorner(trigger: FeedbackTrigger, dueAt: number) {
    const alreadyHeld = armedRef.current?.heldForCorner === true
    armedRef.current = {
      event: trigger.event,
      dueAt,
      heldForCorner: true,
    }
    if (!alreadyHeld) {
      trackSuppressed(trigger.event, 'corner_occupied')
    }
  }

  function commitOpen(trigger: FeedbackTrigger) {
    if (cornerOccupiedRef.current) {
      holdForCorner(trigger, Date.now())
      return
    }

    armedRef.current = null

    if (isOpenRef.current) {
      trackSuppressed(trigger.event, 'toast_already_open')
      return
    }

    if (
      isFeedbackCoolingDown() ||
      isGlobalCooldownActive() ||
      isTriggerCoolingDown(trigger.event, trigger.cooldownDays)
    ) {
      trackSuppressed(trigger.event, 'cooldown')
      return
    }

    // Close the gap before the next render flips `isOpen`.
    isOpenRef.current = true
    setTriggerCooldown(trigger.event)
    setGlobalCooldown()
    track(AnalyticsEvents.FEEDBACK_AUTO_TRIGGERED, {
      trigger_event: trigger.event,
      delay_sec: trigger.delaySec,
      survey_key: trigger.surveyKey,
    })
    openRef.current({
      triggerType: 'auto',
      triggerEvent: trigger.event,
      surveyKey: trigger.surveyKey,
    })
  }

  const commitOpenRef = useRef(commitOpen)
  commitOpenRef.current = commitOpen
  const holdForCornerRef = useRef(holdForCorner)
  holdForCornerRef.current = holdForCorner
  const trackSuppressedRef = useRef(trackSuppressed)
  trackSuppressedRef.current = trackSuppressed

  function schedule(trigger: FeedbackTrigger, dueAt: number) {
    clearPendingTimer()
    armedRef.current = {
      event: trigger.event,
      dueAt,
      heldForCorner: armedRef.current?.heldForCorner === true,
    }
    const wait = Math.max(0, dueAt - Date.now())
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      commitOpenRef.current(trigger)
    }, wait)
  }

  const scheduleRef = useRef(schedule)
  scheduleRef.current = schedule

  function resumeHeldPrompt() {
    if (cornerOccupiedRef.current || timerRef.current) return
    const armed = armedRef.current
    if (!armed?.heldForCorner) return
    const trigger = FEEDBACK_TRIGGERS.find((item) => item.event === armed.event)
    if (!trigger) {
      armedRef.current = null
      return
    }
    scheduleRef.current(trigger, armed.dueAt)
  }

  const resumeHeldPromptRef = useRef(resumeHeldPrompt)
  resumeHeldPromptRef.current = resumeHeldPrompt

  useEffect(() => {
    if (isOpen) {
      clearPendingTimer()
      armedRef.current = null
    }
  }, [isOpen])

  // Leaving a clear surface cancels a prompt that has not been shown yet,
  // matching the previous dwell timer. A prompt already held for a dense
  // corner stays armed. Pathname cleanup drops the timer, so the setup of
  // the next page starts it again when that page is also clear. Otherwise
  // Summary → Home would leave `armedRef` set and block every later prompt.
  useEffect(() => {
    resumeHeldPromptRef.current()
    return () => {
      const armed = armedRef.current
      clearPendingTimer()
      if (armed && (armed.heldForCorner || cornerOccupiedRef.current)) {
        armedRef.current = { ...armed, heldForCorner: true }
        return
      }
      armedRef.current = null
    }
  }, [pathname])

  useEffect(() => {
    resumeHeldPromptRef.current()
  }, [cornerOccupied])

  useEffect(() => {
    const interval = setInterval(() => {
      const last = getLastAnalyticsEvent()
      if (!last) return

      const eventKey = `${last.event}:${JSON.stringify(last.props)}`
      if (eventKey === lastSeenEventRef.current) return
      lastSeenEventRef.current = eventKey

      if (isOpenRef.current || timerRef.current || armedRef.current) return

      const trigger = FEEDBACK_TRIGGERS.find((item) => item.event === last.event)
      if (!trigger) return

      if (trigger.minSessionCount) {
        return
      }

      if (
        isFeedbackCoolingDown() ||
        isGlobalCooldownActive() ||
        isTriggerCoolingDown(trigger.event, trigger.cooldownDays)
      ) {
        trackSuppressedRef.current(trigger.event, 'cooldown')
        return
      }

      const dueAt = Date.now() + trigger.delaySec * 1000
      if (cornerOccupiedRef.current) {
        holdForCornerRef.current(trigger, dueAt)
        return
      }

      scheduleRef.current(trigger, dueAt)
    }, 500)

    return () => clearInterval(interval)
  }, [open])
}
